// spec-007-aprovacao-de-skills-por-risco ([T021], `contracts/ui-components.md`
// rodada 3) — telas "O que a IA aprendeu", "Skills da IA" e "Aguardando você".
//
// Vive em arquivo PRÓPRIO — `Text.js` já estava perto do teto de 500 linhas
// (CLAUDE.md do monorepo: arquivos ≤500 linhas, quebrar por responsabilidade
// ao crescer). `Text.js` reexporta cada export daqui — mesmo padrão de
// `MEMORIA_TEXT`/`Text.memoria.js` (spec-002 [T016]).
//
// Textos literais vieram do contrato (`contracts/ui-components.md` §1-9) —
// não invente copy nova sem conferir o documento. Onde o contrato não dá o
// texto literal (títulos de página, avisos auxiliares), a copy segue o
// registro da casa (frases curtas, sempre com a solução/próximo passo).

const PROIBIDO_GESTOR_TEXT =
  'Esta área está disponível apenas para gestores do escritório.'

// ─── Abas da timeline (§1, seletor de abas com contadores) ─────────────────
// `value` casa com `filtros.aba` (`view-aprendizado-ia.vue`) e com as chaves
// do objeto `abas` (`{publicado, aguardando, nao_publicado, desfeito}`).
export const ABAS_APRENDIZADO = [
  { value: 'publicado', label: 'Publicado' },
  { value: 'aguardando', label: 'Aguardando você' },
  { value: 'nao_publicado', label: 'Não publicado' },
  { value: 'desfeito', label: 'Desfeito' },
]

// ─── Origem do aprendizado (chip + filtro, §2/§10) ──────────────────────────
// As 9 chaves de baixo são as do contrato (rodada 3, 2026-08-28): Chat /
// Extensão / Reunião / Ensaio / Central de IA / Comitê / Curadoria / API /
// Gestor — chave em `ORIGENS_APRENDIZADO` (`constants/reflexao.py`), valor no
// rótulo humano.
//
// ★ `instancia` — 10ª origem, fechada em 30/08/2026 (achado do team-lead após
// T021, decisão do orquestrador registrada em `aawz_intelligence_v2/
// constants/aprovacoes.py` nota 1 e `approvals/registro.py`): a v638 real tem
// 10 valores no CHECK, dois dias depois da rodada 3 ter fixado só 9 rótulos em
// `ui-components.md`. Rótulo próprio, no molde dos outros 9 (substantivo
// curto) — sem uma copy do contrato pra seguir, porque o contrato nunca
// chegou a nomear esta origem.
export const ORIGEM_LABELS = {
  chat: 'Chat',
  extensao: 'Extensão',
  pipeline: 'Reunião',
  ensaio: 'Ensaio',
  central: 'Central de IA',
  comite: 'Comitê',
  curator: 'Curadoria',
  api: 'API',
  gestor: 'Gestor',
  instancia: 'Instância',
}

// ─── Risco (`_aprovacoes.risk_level` — `constants/aprovacoes.py::NIVEIS`) ──
export const RISCO_LABELS = {
  baixo: 'Baixo',
  medio: 'Médio',
  alto: 'Alto',
}

// Cor por nível, na mesma ordem de `RISCO_LABELS` — `contracts/ui-
// components.md` §2: "baixo→success, medio→warning, alto→danger — SEMPRE
// com texto". Consumido pelo `BaseStatus :color="RISCO_COLORS[nivel]"` do
// `publicacao-card.vue` (T025); vive aqui para não duplicar o mapeamento.
export const RISCO_COLORS = {
  baixo: 'success',
  medio: 'warning',
  alto: 'danger',
}

// ─── Motivos de risco humanizados (`risk_reasons`, AC-2.6) ─────────────────
// `approvals/risco.py` grava o motivo com o PARÂMETRO embutido na própria
// string (`WRITE_TOOL:<tool>`, `DOMINIO_ALTO:<dominio>`, `TIPO:<proposal_
// type>`, `TOOL_DESCONHECIDA:<tool>`, `DECLARADO:<nivel>`) — por isso essas
// entradas são FUNÇÕES `(parametro) => texto`; os demais motivos (sem
// parâmetro) e os 2 motivos de eval (`CORPO_CITA_CONFIRMACAO`,
// `DATASET_AUSENTE`) são strings diretas. Nunca use o motivo cru na tela —
// sempre pelo resolver `humanizarMotivoDeRisco` abaixo.
export const RISCO_MOTIVO_LABELS = {
  // — com parâmetro embutido —
  DOMINIO_ALTO: (dominio) => `domínio de alto risco (${dominio})`,
  TIPO: (tipo) => `mexe em uma skill já publicada (${tipo})`,
  TOOL_DESCONHECIDA: () => 'usa uma ferramenta que a IA ainda não conhece',
  DECLARADO: (nivel) => `risco declarado como ${RISCO_LABELS[nivel] || nivel} na origem`,
  // `acaoDaTool` é opcional — quem chama (o card, que tem `TOOL_LABELS`/o
  // registry) pode passar o nome humano da ação ("criar tarefa"); sem ele o
  // texto fica genérico, mas NUNCA mostra o slug da tool cru.
  WRITE_TOOL: (_tool, acaoDaTool) =>
    acaoDaTool
      ? `usa ferramenta de escrita (${acaoDaTool})`
      : 'usa ferramenta de escrita',
  // `ALTERACAO_FINANCEIRA:create_movimentacao` — o exemplo do contrato NÃO
  // repete o nome da tool no texto (ela já apareceu no motivo WRITE_TOOL
  // irmão, sempre presente junto — `approvals/risco.py:164-167`).
  ALTERACAO_FINANCEIRA: () => 'altera a carteira do cliente',
  // — sem parâmetro —
  FORMATACAO: 'é só formatação, sem ferramenta e sem domínio sensível',
  PROCEDIMENTO_ANALITICO: 'muda um procedimento analítico existente',
  DENYLIST: 'contém um termo bloqueado',
  REMOCAO: 'remove algo que a IA sabia',
  PREFERENCIA_EXPLICITA: 'é uma preferência explícita, com fonte forte',
  CLIENTE_SEM_FONTE_ALTA: 'memória do cliente sem fonte forte',
  OFFICE_SEM_FONTE_ALTA: 'memória do escritório sem fonte forte',
  PROCEDIMENTO_AGENTE: 'muda um procedimento do agente',
  INFERENCIA: 'foi inferido, sem confirmação explícita',
  // spec-015 ADR-015-07 — `approvals/risco.py::_piso_da_fonte`: memória cuja
  // evidência veio de um AGENTE DE SISTEMA (transcrição/documento lido por
  // ele) nasce `medio` com este motivo, em vez de `PREFERENCIA_EXPLICITA`.
  // É o motivo mais comum da fila de reunião — sem label, a tela mostrava o
  // código cru (mesmo defeito, em outro caminho).
  INFERENCIA_DE_AGENTE: 'foi inferido por um agente a partir de uma reunião ou documento',
  KNOWLEDGE_CONFLITO: 'conflita com uma regra oficial do escritório',
  KNOWLEDGE_OFICIAL: 'vira conhecimento oficial do escritório',
  // — motivos de eval (`approvals/eval_gate.py`/`eval_checks.py`) —
  CORPO_CITA_CONFIRMACAO: 'o corpo não pede confirmação antes de escrever',
  DATASET_AUSENTE: 'ainda não temos um conjunto de avaliação para este domínio',
  // — motivos do CAMINHO DA 005 (`learning/risco.py` — MemoryService.commit
  // NÃO passa por `approvals/risco.py`) — sem estas entradas o cartão de
  // aprendizado gravado por esse
  // pipeline mostrava o código cru (ex.: "ESCOPO_COMPARTILHADO" na tela).
  // `RISCO_DECLARADO_ALTO`/`SKILL_MODIFY_PUBLICADA`/`SKILL_RISCO_DECLARADO_
  // MEDIO`/`SKILL_ARCHIVE`/`SKILL_MERGE`/`SKILL_SPLIT`/`REMOVE`/`SEM_
  // EVIDENCIA`/`ESCOPO_COMPARTILHADO` são strings literais de
  // `learning/risco.py`; os 8 valores de `SourceType` (exceto
  // `user_explicit`, que não emite motivo) aparecem CRUS quando
  // `ProfileUpdateCandidate`/`MemoryCandidate` tem confiança média/baixa —
  // mesmo vocabulário de `contratos/modelos/contexto.py::SourceType`.
  RISCO_DECLARADO_ALTO: 'risco declarado como alto na origem',
  SKILL_MODIFY_PUBLICADA: 'muda uma skill já publicada',
  SKILL_RISCO_DECLARADO_MEDIO: 'risco declarado como médio na origem',
  SKILL_ARCHIVE: 'arquiva uma skill existente',
  SKILL_MERGE: 'combina skills existentes',
  SKILL_SPLIT: 'divide uma skill existente',
  REMOVE: 'remove algo que a IA sabia',
  SEM_EVIDENCIA: 'não tem proveniência (evidência) registrada',
  ESCOPO_COMPARTILHADO: 'memória compartilhada no escritório (cliente ou escritório)',
  system_policy: 'vem de uma política do sistema',
  organization_policy: 'vem de uma política da organização',
  trusted_internal_tool: 'vem de uma ferramenta interna confiável',
  official_business_document: 'vem de um documento oficial do negócio',
  agent_observation: 'foi observado pelo agente',
  agent_inference: 'foi inferido pelo agente, sem confirmação explícita',
  external_web: 'vem de uma busca externa na web',
  retrieved_arbitrary: 'vem de conteúdo recuperado sem curadoria',
}

/**
 * Resolve um motivo cru (`risk_reasons[i]`) para o texto humano. Nunca
 * devolve o slug de uma tool — motivo desconhecido cai no próprio texto
 * (sinal para atualizar o mapa acima, mesma disciplina de `TOOL_LABELS`).
 * @param {string} motivo
 * @param {{acaoDaTool?: string}} [opts]
 */
export const humanizarMotivoDeRisco = (motivo, opts) => {
  if (!motivo) return ''
  const acaoDaTool = opts && opts.acaoDaTool
  const entrada = RISCO_MOTIVO_LABELS[motivo]
  if (typeof entrada === 'string') return entrada
  if (typeof entrada === 'function') return entrada()
  const indice = motivo.indexOf(':')
  if (indice === -1) return motivo
  const prefixo = motivo.slice(0, indice)
  const parametro = motivo.slice(indice + 1)
  const fn = RISCO_MOTIVO_LABELS[prefixo]
  if (typeof fn !== 'function') return motivo
  return prefixo === 'WRITE_TOOL' ? fn(parametro, acaoDaTool) : fn(parametro)
}

// ─── Eval v0 (chip da eval, §2) ─────────────────────────────────────────────
// Chaves por `eval_status` (`approvals/modelos.py`: nao_aplicavel | pendente
// | verde | vermelho) + `sem_dataset`, estado DERIVADO (`eval_status ===
// 'pendente'` com motivo `DATASET_AUSENTE` — `eval_checks.py:174-175`): quem
// decide qual dos dois "pendente" usar é o card (T025), que tem acesso ao
// motivo; aqui só os 5 textos do contrato.
export const EVAL_LABELS = {
  verde: 'Eval verde',
  vermelho: 'Eval reprovada',
  pendente: 'Aguardando eval',
  nao_aplicavel: 'Sem eval (baixo)',
  sem_dataset: 'Sem dataset do domínio',
}

// ─── Procedência da memória (publicacao-card.vue, §2, spec-015 [T306] US-5
// AC2: "aponta a reunião: o run e o documento/transcrição que a originou") ──
// `evidencia_resumo.fonte === 'agente_sistema'` é o sinal mais forte —
// carrega o produtor (slug do agente de sistema, ex.: `analista-reuniao`,
// resolvido pelo nome via `Hubs.js::rotuloDoAgente`) e o apontador de
// origem: `id_transcricao` OU `id_documento` (nunca os dois —
// `EvidenciaDeOrigem.tipo` no backend é um enum de um valor só). Sem
// `fonte` (linha antiga, antes de [T303]), `id_conversa` continua o sinal
// de conversa, e `origem === 'pipeline'` o fallback de compatibilidade
// para reunião sem procedência detalhada.
export const PROCEDENCIA_LABELS = {
  reuniao: 'Reunião',
  conversa: 'Conversa',
}
export const PROCEDENCIA_NAO_REGISTRADA = 'Origem não registrada'

// Rótulo do tipo de apontador de origem — chave casa com
// `EvidenciaResumo.id_transcricao`/`.id_documento` (só um dos dois vem
// preenchido por vez).
export const PROCEDENCIA_ORIGEM_TIPO_LABELS = {
  transcricao: 'transcrição',
  documento: 'documento',
}

// Tamanho do id ENCURTADO na linha visível (id completo só no
// `title`/tooltip) — nomeado para nunca virar um número mágico solto no
// componente.
export const PROCEDENCIA_TAMANHO_ID_ENCURTADO = 8

// Tooltip do apontador de origem: id completo + `id_run` (o run que gerou
// o candidato — técnico, por isso só no tooltip, nunca na linha visível).
export const procedenciaTooltipOrigem = (idCompleto, idRun) => {
  const partes = ['id completo: ' + idCompleto]
  if (idRun) partes.push('run: ' + idRun)
  return partes.join(' · ')
}

// ─── Opções de filtro da timeline (view-aprendizado-ia.vue [T031], §1) ─────
// Vivem aqui (não na view) por causa do gate `design-system:no-regression`
// ("Constantes de domínio OPTIONS/... inline em view"). "Todos" (`value:
// null`) sempre primeiro — `formatQueryParams` remove `null` da query, então
// "Todos" é literalmente "nenhum filtro". Risco/origem derivam dos mapas
// acima (uma fonte só do rótulo); tipo/escopo são enums fechados do banco
// (`resource_type`/`scope`, `spec-005/data-model.md` §1.1) sem mapa de rótulo
// próprio — o `agente` da tela NÃO tem opção fixa aqui: as opções vêm dos
// agentes presentes na página carregada (computed `agentesFiltro` na view).
export const RISCO_OPTIONS = [
  { text: 'Todos', value: null },
  ...Object.keys(RISCO_LABELS).map((value) => ({ text: RISCO_LABELS[value], value })),
]

export const ORIGEM_OPTIONS = [
  { text: 'Todos', value: null },
  ...Object.keys(ORIGEM_LABELS).map((value) => ({ text: ORIGEM_LABELS[value], value })),
]

export const RESOURCE_TYPE_OPTIONS = [
  { text: 'Todos', value: null },
  { text: 'Memória', value: 'memory' },
  { text: 'Skill', value: 'skill' },
  { text: 'Conhecimento oficial', value: 'knowledge' },
]

// `scope IN ('user','agent','client','office')` — só se aplica a `resource_
// type='memory'` (NULL em skill/knowledge); mesmo assim entra como filtro
// único da timeline, não um por tipo.
export const SCOPE_OPTIONS = [
  { text: 'Todos', value: null },
  { text: 'Você', value: 'user' },
  { text: 'Agente', value: 'agent' },
  { text: 'Cliente', value: 'client' },
  { text: 'Escritório', value: 'office' },
]

export const PERIODO_OPTIONS = [
  { text: '7 dias', value: 7 },
  { text: '30 dias', value: 30 },
  { text: '90 dias', value: 90 },
]

// ─── APRENDIZADO_TEXT — view-aprendizado-ia.vue + os 3 modais (§1,4,5,6) ───
export const APRENDIZADO_TEXT = {
  pageTitle: 'O que a IA aprendeu',
  pageDescription:
    'Tudo o que a IA propôs, publicou e desfez, com o motivo de cada decisão.',
  badgeAguardando: (n) => `${n} aguardando`,
  atualizar: 'Atualizar',
  faixaDesligada:
    'Aprovação humana: desligada. A IA publica sozinha; você pode desfazer',
  faixaLigada: 'Aprovação humana: ligada',
  // 4 estados obrigatórios (§1)
  noData:
    'A IA ainda não aprendeu nada neste escritório. Quando aprender, aparece aqui, e você pode desfazer em um clique.',
  noDataAguardando:
    'Nada esperando por você. A IA publica sozinha o que passa na avaliação.',
  noDataNaoPublicado: 'Nada foi barrado pela avaliação neste período.',
  noDataDesfeito: 'Você ainda não desfez nada.',
  erroTitulo: 'Não foi possível carregar a timeline',
  retry: 'Tentar de novo',
  proibido: PROIBIDO_GESTOR_TEXT,
  // publicacao-card.vue (§2)
  verEvidencia: 'ver evidência',
  verDiff: 'ver diff',
  desfazer: 'Desfazer',
  editar: 'Editar',
  recusar: 'Recusar',
  publicar: 'Publicar',
  publicarMesmoAssim: 'Publicar mesmo assim',
  publicarComEdicao: 'Publicar com edição',
  // aprovacao-diff.vue (§3)
  diffVazio: 'sem alterações',
  // publicacao-reverter.vue (§4)
  reverterTitulo: (entidadeLabel, nome) => `Desfazer: ${entidadeLabel} · ${nome}`,
  reverterSkillModify: (versaoNova) =>
    `A skill volta ao conteúdo anterior como uma versão nova (v${versaoNova}). ` +
    'Esta publicação fica registrada como desfeita.',
  reverterSkillCreate: 'A skill é arquivada.',
  reverterMemoriaModify: 'A memória anterior volta a valer; a atual é arquivada.',
  reverterMemoriaCreate: 'A memória é removida do que a IA sabe.',
  motivoLabel: 'Motivo (opcional)',
  // aprovacao-recusar.vue (§5)
  recusarMotivoLabel: 'Motivo',
  // aprovacao-editar.vue (§6)
  editarTituloPublicar: 'Publicar com edição',
  editarTituloEditar: 'Editar publicação',
  preVisualizar: 'Pré-visualizar',
  // comuns aos 3 modais
  confirmar: 'Confirmar',
  cancelar: 'Cancelar',
}

// ─── SKILLS_TEXT — view-skills-ia.vue + skill-tabela.vue + skill-editar (§7-9) ──
export const SKILLS_TEXT = {
  pageTitle: 'Skills da IA',
  pageDescription:
    'Catálogo de procedimentos que os agentes seguem: crie, edite, publique e acompanhe o histórico de versões.',
  novaSkill: 'Nova skill',
  // Título/subtítulo do BaseTable (DS: toda listagem leva os dois — o título
  // da PÁGINA já vem do NavBarNavigation, este é o do bloco) + a busca da
  // toolbar acima da tabela.
  tableTitle: 'Catálogo de skills',
  tableSubtitle: 'Expanda uma linha para ler o corpo e o histórico de versões.',
  buscaPlaceholder: 'Buscar skill',
  ledgerTitle: 'Histórico de versões',
  noData: 'Nenhuma skill neste filtro.',
  erroTitulo: 'Não foi possível carregar as skills',
  erroDetalhe: 'Não foi possível carregar o detalhe desta skill.',
  retry: 'Tentar de novo',
  proibido: PROIBIDO_GESTOR_TEXT,
  editar: 'Editar',
  arquivar: 'Arquivar',
  reativar: 'Reativar',
  restaurarEstaVersao: 'Restaurar esta versão',
  restaurarConfirmTitulo: 'Restaurar versão',
  restaurarConfirmTexto: (versao, versaoNova) =>
    `A v${versao} vira a versão nova (v${versaoNova})`,
  globalTooltip: 'catálogo AAWZ, somente leitura',
  escritaTooltip: 'skill de escrita, two-step obrigatório',
  chipSemUsoRecente: 'Sem uso recente',
  chipCandidata: 'Candidata, ainda não vale',
  avisoWriteTool:
    'esta skill será de alto risco: o corpo precisa pedir confirmação antes de escrever',
  // skill-motivo.vue — substitui o `window.prompt` provisório de
  // `view-skills-ia.vue` (motivo obrigatório em `lifecycle`, §7).
  motivoTitulo: (para) => (para === 'archived' ? 'Arquivar skill' : 'Reativar skill'),
  motivoLabel: 'Motivo',
  motivoPlaceholder: 'Explique o motivo desta ação',
  motivoObrigatorio: 'Informe um motivo para continuar.',
  cancelar: 'Cancelar',
}

// ─── Opções de filtro do catálogo (view-skills-ia.vue [T032], §7) ──────────
// Vivem aqui (não na view) pelo mesmo gate `design-system:no-regression`
// ("Constantes de domínio OPTIONS/... inline em view") citado acima para
// `view-aprendizado-ia.vue`. "Todos" (`value: null`) sempre primeiro.
//
// Espelho de `constants/skills.py::SCOPES` (`aawz_intelligence_v2`) — os 3
// escopos + "Todos" do filtro.
export const SCOPE_FILTRO_OPTIONS = [
  { text: 'Todos', value: null },
  { text: 'AAWZ', value: 'global' },
  { text: 'Escritório', value: 'office' },
  { text: 'Agente', value: 'agent' },
]

// `lifecycle` do filtro tem 5 valores: os 4 da tela (`SkillTabela.
// ESTADO_LABELS`) + o `'stale'` VIRTUAL que `SkillsGestaoUsecase.listar`
// deriva de `stale_since` (`resource_skills_gestao.py:150-161`) — não é uma
// coluna real, mas é um `choice` aceito pelo reqparse do GET /skills.
export const LIFECYCLE_FILTRO_OPTIONS = [
  { text: 'Todos', value: null },
  { text: 'Ativas', value: 'published' },
  { text: 'Sem uso recente', value: 'stale' },
  { text: 'Arquivadas', value: 'archived' },
  { text: 'Candidatas', value: 'candidate' },
]

// Espelho de `constants/skills.py::Dominio` (11 membros) — mesmo mapa de
// `modal/skill-editar/skill-editar.vue` (`DOMINIO_OPTIONS`); duplicado aqui
// pela mesma disciplina já registrada em `skill-tabela.vue` (`DOMINIO_
// LABELS`): os 3 arquivos derivam do mesmo enum do backend, sem constante
// module-wide compartilhada — fora do escopo desta task tocar os outros 2
// (nenhum dos dois está sob `views/`, então o gate acima não os alcança).
export const DOMINIO_FILTRO_OPTIONS = [
  { text: 'Todos', value: null },
  { text: 'Relacionamento com cliente', value: 'client_relationship' },
  { text: 'Wealth', value: 'wealth' },
  { text: 'Planejamento financeiro', value: 'financial_planning' },
  { text: 'Compliance', value: 'compliance' },
  { text: 'Faturamento', value: 'billing' },
  { text: 'Gestão', value: 'management' },
  { text: 'Controladoria', value: 'controller' },
  { text: 'Parceria', value: 'partnership' },
  { text: 'Assessoria', value: 'advisory' },
  { text: 'Sistema', value: 'sistema' },
  { text: 'Conhecimento', value: 'knowledge' },
]

// Vocabulário PRÓPRIO de `AcaoPendenteItem.fonte`
// (`['chat','reuniao','central'].includes`), distinto dos 9 `ORIGEM_LABELS`
// acima (aqueles são de `AprendizadoItem.origem`, eixo diferente).
const FONTE_LABELS = {
  chat: 'Chat',
  reuniao: 'Reunião',
  central: 'Central de IA',
}

// ─── ACOES_TEXT — view-acoes-ia.vue (§6.1, US-9) ───────────────────────────
export const ACOES_TEXT = {
  pageTitle: 'Aguardando você',
  pageDescription: 'Ações da IA que esperam a sua confirmação.',
  badge: (n) => `${n} ações`,
  noData: 'Nada esperando por você.',
  erroTitulo: 'Não foi possível carregar as ações',
  retry: 'Tentar de novo',
  confirmar: 'Confirmar',
  descartar: 'Descartar',
  descartarConfirmTitulo: 'Descartar ação',
  descartarConfirmTexto: 'Descartar esta ação? Ela não gera nenhum registro.',
  // chip da fonte, §0/§2
  fonteLabels: FONTE_LABELS,
}

// Filtro "fonte" da view (§6.1) — mesmo molde de `RISCO_OPTIONS`/
// `ORIGEM_OPTIONS`: "Todas" (`value: null`) primeiro, resto derivado do
// mapa de rótulos (fonte única).
export const FONTE_OPTIONS = [
  { text: 'Todas', value: null },
  ...Object.keys(FONTE_LABELS).map((value) => ({ text: FONTE_LABELS[value], value })),
]

// ─── SKILLS_TABLE_HEADERS — skill-tabela.vue (§8) ──────────────────────────
// `value` casa com os campos de `SkillItem` (`approvals/skills_gestao.py::
// _item_de`): id, name, description, scope, agent{slug,nome}, dominio,
// lifecycle, version, risk_level, escrita, pinned, view_count, use_count,
// last_used_at, updated_at, editavel.
// Ledger (histórico de versões) da linha expandida — BaseTable aninhado,
// mesma disciplina de rótulo local do `ia-changelog-table`.
export const SKILLS_LEDGER_HEADERS = [
  { text: 'Ação', value: 'acaoRotulo', sortable: false },
  { text: 'Ator', value: 'ator', sortable: false },
  { text: 'Versão', value: 'version', sortable: false, align: 'center' },
  { text: 'Nota', value: 'nota', sortable: false },
  { text: 'Quando', value: 'quandoFormatado', sortable: false },
  { text: '', value: 'actions', sortable: false, align: 'center', width: '64px' },
]

export const SKILLS_TABLE_HEADERS = [
  { text: 'Nome', value: 'name', sortable: true },
  { text: 'Descrição', value: 'description', sortable: false },
  { text: 'Escopo', value: 'scope', sortable: true },
  { text: 'Agente', value: 'agent', sortable: false },
  { text: 'Domínio', value: 'dominio', sortable: true },
  { text: 'Estado', value: 'lifecycle', sortable: true },
  { text: 'Versão', value: 'version', sortable: true, align: 'center' },
  { text: 'Risco', value: 'risk_level', sortable: true },
  { text: 'Uso', value: 'use_count', sortable: true, align: 'center' },
  {
    text: '',
    value: 'actions',
    align: 'center',
    sortable: false,
    width: '80px',
  },
]
